/**
 * Parameter value shapes monday puts inside `legacy_automations[].config`
 * (`specs/M6-A3-automation-catalog.md` §9, `discovery/automations/api/catalog.md` §4, and the
 * sandbox pass `discovery/automations/sandbox-experiments.md`).
 *
 * The one rule that governs this file: **every observed variant must parse**, because the
 * importer sees them all. monday writes numbers as ints and as strings, column references as a
 * bare id and as an object, operators as a bare string and as `{title, value}`, and a status
 * value either as `{index}` or as the wildcard `{"$any$": true}`. Normalising them here is what
 * keeps the blocks free of shape checks.
 */
import { z } from 'zod';
/** monday writes `2`, `"2"` and `"-2"` for the same number (`catalog.md` §4.2). */
export const numberish = z
    .union([z.number(), z.string()])
    .transform((raw, ctx) => {
    const value = typeof raw === 'number' ? raw : Number(raw.trim());
    if (!Number.isFinite(value)) {
        ctx.addIssue({ code: 'custom', message: `not a number: ${JSON.stringify(raw)}` });
        return z.NEVER;
    }
    return value;
});
export const integerish = numberish.pipe(z.number().int());
/**
 * A **boolean the way monday's builder stores one** (P-290, round 11d).
 *
 * Its "apply to existing items" answer is not a primitive: the field is a `list` control
 * (`recipe-catalogue-dynamic.json`: `{type: "list", options: [{title: "True", value: "true"},
 * {title: "False", value: "false"}]}`), so what lands in the config is the selected **option**.
 * Automation 371023875 on board 4066567709 carries `shouldApplyOnExistingItems: {value: true}`
 * while the catalogue's own options carry the string `"true"` — and `z.boolean()` rejected both,
 * which is `linkageBoards` all over again: monday's shape, our schema's guess.
 *
 * Accepts `true`, `"true"`, `{value: true}` and `{title, value: "false"}`, and reads `null` as
 * "not answered" (`undefined`), which is what an optional field means.
 */
export const booleanish = z
    .union([
    z.boolean(),
    z.string(),
    z.looseObject({ value: z.union([z.boolean(), z.string()]) }),
    z.null(),
])
    .transform((raw, ctx) => {
    const flag = (value) => {
        if (typeof value === 'boolean')
            return value;
        const text = value.trim().toLowerCase();
        if (text === 'true')
            return true;
        if (text === 'false')
            return false;
        return undefined;
    };
    const value = raw === null ? undefined : typeof raw === 'object' ? flag(raw.value) : flag(raw);
    if (value === undefined && raw !== null) {
        ctx.addIssue({ code: 'custom', message: 'not a boolean: expected true/false, "true"/"false" or {value}' });
        return z.NEVER;
    }
    return value;
});
/**
 * A column reference. `static` recipes store a bare id (`"status9"`), `custom` recipes an object
 * with the legacy type name and monday's `invalid` marker, which is **preserved**: it is how a
 * broken reference survives an import (`specs/M6-A1` §9).
 */
export const columnRefSchema = z
    .union([
    z.string().min(1),
    z.object({
        boardId: numberish.optional(),
        columnId: z.string().min(1),
        columnType: z.string().optional(),
        isSubitemColumn: z.boolean().optional(),
        includeTime: z.boolean().optional(),
        invalid: z.boolean().optional(),
    }),
])
    .transform((raw) => (typeof raw === 'string' ? { columnId: raw } : raw));
/** `$any$` — the "Anything" entry of monday's label picker (sandbox E13). */
export const ANY_VALUE = '$any$';
export const isAnyValue = (value) => typeof value === 'object' && value !== null && value[ANY_VALUE] === true;
/**
 * A status/dropdown label **index**. The label text is never in the config — only the index
 * (`column-value-shapes.md` §status), which is also why nothing may renumber labels on import.
 */
export const labelValueSchema = z
    .union([
    z.object({ $any$: z.literal(true), invalid: z.boolean().optional() }),
    z.object({ index: integerish, invalid: z.boolean().optional() }),
    integerish.transform((index) => ({ index })),
])
    .transform((raw) => raw);
export const labelValueListSchema = z
    .union([labelValueSchema, z.array(labelValueSchema)])
    .transform((raw) => (Array.isArray(raw) ? raw : [raw]));
/**
 * The ids in a "someone" slot (blocks `50`, `1810`) — **P-278**.
 *
 * monday writes this slot four ways and the schema used to accept only two of them
 * (`integerish | integerish[]`), so a recipe whose "someone" was stored as the people **cell
 * value** object failed `paramsSchema.parse` with
 * `invalid_union … expected number|string|array, received object`. That ZodError escaped the
 * engine into the outbox bridge, which dropped the whole `cell.changed` event — which is how a
 * mass edit stopped firing *every* automation on the board. The rule of this file applies:
 * **every observed variant must parse**.
 *
 * Only the ids survive. A team entry keeps its id like any other: `1810` matches by id and
 * never expands membership (`specs/M6-A3` §11), and `50` writes `kind: 'person'` — whether
 * monday would re-assign a *team* through this slot is q573, and the conservative reading is
 * the one already shipped, so the shape is accepted without changing what is written.
 */
const personEntrySchema = z.object({ id: numberish, kind: z.string().optional() });
export const personIdListSchema = z
    .union([
    integerish,
    z.array(z.union([integerish, personEntrySchema])),
    z.object({ personsAndTeams: z.array(personEntrySchema) }),
    personEntrySchema,
])
    .transform((raw) => {
    if (typeof raw === 'number')
        return [raw];
    if (Array.isArray(raw))
        return raw.map((entry) => (typeof entry === 'number' ? entry : entry.id));
    if ('personsAndTeams' in raw)
        return raw.personsAndTeams.map((entry) => entry.id);
    return [raw.id];
});
/** `"is"` / `"isnot"` bare (5+8 rows) and `{title, value}` (13+4 rows) mean the same thing. */
export const isConditionSchema = z
    .union([
    z.enum(['is', 'isnot']),
    z.object({ title: z.string().optional(), value: z.enum(['is', 'isnot']), invalid: z.boolean().optional() }),
])
    .transform((raw) => (typeof raw === 'string' ? raw : raw.value));
/** `contains one of` / `contains only` / `contains all` over label index **sets**. */
export const dropdownConditionSchema = z
    .union([
    z.enum(['one', 'only', 'all']),
    z.object({ title: z.string().optional(), value: z.enum(['one', 'only', 'all']), invalid: z.boolean().optional() }),
])
    .transform((raw) => (typeof raw === 'string' ? raw : raw.value))
    .default('all');
/**
 * A duration. The builder's `allowedOptions` are `months|weeks|days|businessDays` for date
 * arithmetic and `months|weeks|days|hours|minutes` for the "after some time" delay (sandbox
 * E14), and the legacy shape is a bare `{days}` / `{minutes}` map.
 */
export const DURATION_UNITS = ['months', 'weeks', 'days', 'businessDays', 'hours', 'minutes'];
export const durationSchema = z
    .object({
    months: numberish.optional(),
    weeks: numberish.optional(),
    days: numberish.optional(),
    businessDays: numberish.optional(),
    hours: numberish.optional(),
    minutes: numberish.optional(),
    count: numberish.optional(),
    unit: z.enum(DURATION_UNITS).optional(),
})
    .transform((raw) => {
    if (raw.unit !== undefined && raw.count !== undefined)
        return { [raw.unit]: raw.count };
    const out = {};
    for (const unit of DURATION_UNITS)
        if (raw[unit] !== undefined)
            out[unit] = raw[unit];
    return out;
});
/** A signed shift from a date: `0`, or `{count, unit}` with `count` possibly a string. */
export const offsetSchema = z
    .union([
    z.literal(0),
    z.literal('0'),
    z.object({ count: numberish, unit: z.enum(DURATION_UNITS) }),
])
    .transform((raw) => (raw === 0 || raw === '0' ? { count: 0, unit: 'days' } : raw));
export const OFFSET_NONE = { count: 0, unit: 'days' };
/**
 * `dateTriggerConfig` (block `580`). **`hour` is a UTC hour**, not a local one, and its
 * fractional part is a fraction of an hour (`5.25` = 05:15); `utcDaysDiff` is the day carry of
 * the same local→UTC conversion. Measured twice on the sandbox — see
 * `discovery/automations/sandbox-experiments.md` E3/E4.
 */
export const dateTriggerConfigSchema = z.object({
    hour: numberish.default(0),
    utcDaysDiff: integerish.default(0),
    offset: offsetSchema.optional().transform((value) => value ?? OFFSET_NONE),
    timezone: z.string().default('UTC'),
    invalid: z.boolean().optional(),
    selectedSnoozeOption: z.string().optional(),
});
/** `schedulerConfig` (block `47`). `occurrences` is the interval multiplier (sandbox E1). */
export const RECURRENCE_TYPES = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
export const schedulerConfigSchema = z.object({
    type: z.enum(RECURRENCE_TYPES),
    occurrences: integerish.default(1),
    hour: numberish.default(0),
    /** Weekly: ISO weekday, **Sunday is 7** (sandbox E2). Monthly: day of month. */
    days: z.array(z.union([integerish, z.string()])).default([]).transform((days) => days.map((d) => Number(d))),
    months: z.array(z.union([integerish, z.string()])).default([]).transform((m) => m.map((x) => Number(x))),
    timezone: z.string().default('UTC'),
    invalid: z.boolean().optional(),
});
/**
 * `receiver`. Six picker options, not three — the sandbox capture of "Who to notify?" reads
 * **Me · Team member · People (column) · Subscribers · Team · Guest**, and the email recipes add
 * board subscribers and an email/text column (sandbox E12).
 */
export const RECEIVER_TYPES = ['me', 'user', 'column', 'subscriber', 'team', 'board'];
export const receiverSchema = z.object({
    type: z.enum(RECEIVER_TYPES),
    /** user/team id (int or string), a column id (bare or a full ref), `"item"|"board"`, or null. */
    value: z.union([z.number(), z.string(), z.null(), columnRefSchema]).default(null),
    kind: z.string().optional(),
    invalid: z.boolean().optional(),
});
/** The person ids a people-like cell value holds (`personsAndTeams`, camelCase inside `value`). */
export function personsAndTeamsOf(value) {
    if (typeof value !== 'object' || value === null)
        return [];
    const list = value.personsAndTeams;
    if (!Array.isArray(list))
        return [];
    return list
        .filter((entry) => typeof entry === 'object' && entry !== null)
        .map((entry) => ({ id: Number(entry.id), kind: String(entry.kind ?? 'person') }))
        .filter((entry) => Number.isFinite(entry.id));
}
/** The label index a status/dropdown cell value holds; `null` for an empty cell. */
export function labelIndexOf(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const index = value.index;
    return typeof index === 'number' && Number.isFinite(index) ? index : null;
}
/** The label index **set** a dropdown cell holds. */
export function labelIndexesOf(value) {
    if (typeof value !== 'object' || value === null)
        return [];
    const ids = value.ids;
    if (Array.isArray(ids))
        return ids.map(Number).filter(Number.isFinite);
    const single = labelIndexOf(value);
    return single === null ? [] : [single];
}
/** `$any$` matches every index **and** the empty cell (`specs/M6-A1` §5.3). */
export function labelValueMatches(configured, actual) {
    if (isAnyValue(configured))
        return true;
    return actual !== null && actual === configured.index;
}
//# sourceMappingURL=values.js.map